import { PushpinFilled } from '@ant-design/icons';
import { Badge, Dropdown, Typography } from 'antd';
import clsx from 'clsx';
import { ArrowDownLeft, ArrowUpRight, Ellipsis, Repeat2 } from 'lucide-react';
import { changeStatus, pinnedChat } from '../../redux/actions/contact';
import { formatChatTime } from '../../utils/chatTimeFormat';
import UserAvatar from '../UserAvatar';

import DOMPurify from 'dompurify';
import { markUnread } from '../../redux/actions/message';
import { getLastMessageByType } from '../../utils';

const ChatItem = ({ user, isActive, onClick, onAddContact, isChat }) => {
    const handleAddContact = (user) => {
        onAddContact?.(user);
    };

    const getActionLabel = (user) => {
        if (!user.name) return 'Add Name';
        if (!user.assigned_user_id) return 'Assign contact';
        return null;
    };

    const menuItems = [
        {
            key: 'archive',
            label: user.status !== 2 ? 'Archive' : 'Unarchive',
            onClick: (e) => {
                e.domEvent.stopPropagation();
                changeStatus({
                    id: user.id,
                    status: user.status !== 2 ? 2 : 1,
                });
            },
        },
        {
            key: 'block',
            label: user.status === 0 ? 'Unblock' : 'Block',
            danger: true,
            onClick: (e) => {
                e.domEvent.stopPropagation();
                changeStatus({
                    id: user.id,
                    status: user.status !== 0 ? 0 : 1,
                });
            },
        },
    ];

    if (user?.isReceivedMessage && !user?.unread_count) {
        menuItems.unshift({
            key: 'mark_unread',
            label: 'Mark as unread',
            onClick: (e) => {
                e.domEvent.stopPropagation();
                markUnread(user.id);
            },
        });
    }

    const label = getActionLabel(user);
    if (label) {
        menuItems.unshift({
            key: 'assign_contact',
            label,
            onClick: (e) => {
                e.domEvent.stopPropagation();
                handleAddContact(user);
            },
        });
    }

    if (isChat) {
        menuItems.unshift({
            key: 'pinned',
            label: user.is_pinned ? 'Unpin Chat' : 'Pin Chat',
            onClick: (e) => {
                e.domEvent.stopPropagation();
                pinnedChat({
                    contact_id: user.id,
                    is_pinned: user.is_pinned ? 0 : 1,
                });
            },
        });
    }

    let rawMessage = user?.last_message || getLastMessageByType(user?.last_message_type, user?.isReceivedMessage) || user?.phone;
    const sanitized = DOMPurify.sanitize(rawMessage, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    const plainText = sanitized.replace(/\\n/g, '\n');

    return (
        <div
            className={clsx('chat-item', { active: isActive, blocked: user.status === 0 })}
            onClick={onClick}
        >
            <div className="avatar">
                <Badge
                    count={user?.is_temporary_assignment ? <Repeat2 size={14} /> : null}
                    style={{
                        background: 'var(--highlight-color)',
                        borderRadius: '50%',
                        padding: 2,
                        color: '#000',
                    }}
                    offset={[-5, 30]}
                >
                    <UserAvatar size={40} user={user} type="CHAT_ITEM" />
                </Badge>
            </div>
            <div className="chat-info">
                <div className="chat-info-header">
                    <Badge
                        count={
                            user?.is_pinned ? (
                                <PushpinFilled style={{ color: 'var(--danger-color)', fontSize: 12 }} />
                            ) : (
                                0
                            )
                        }
                        size="small"
                        offset={[10, 0]}
                    >
                        <Typography.Title level={5} className="title" ellipsis={{ rows: 1, tooltip: true }}>
                            {user?.customer_code && (
                                <span className="customer-code">[{user.customer_code}] </span>
                            )}
                            {user?.name || user?.phone}
                        </Typography.Title>
                    </Badge>
                    <div className="right-container">
                        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                            <div className="action-btn" onClick={(e) => e.stopPropagation()}>
                                <Ellipsis size={18} />
                            </div>
                        </Dropdown>
                        <p className="time">
                            {user?.last_message_time && formatChatTime(user?.last_message_time)}
                        </p>
                    </div>
                </div>

                <div className="chat-info-bottom">
                    <p className="truncate">
                        {user?.isReceivedMessage ? (
                            <ArrowDownLeft size={14} color="var(--highlight-color)" className="mr-1" />
                        ) : (
                            <ArrowUpRight size={14} color="var(--primary-color)" className="mr-1" />
                        )}
                        {plainText}
                    </p>
                    {user?.last_message_time && (
                        <Badge count={user?.unread_count} style={{ background: 'var(--primary-color)' }} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatItem;
